import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { catchError, debounceTime, distinctUntilChanged, filter, finalize, forkJoin, Observable, of, startWith, Subject, switchMap, tap } from 'rxjs';

import CustomValidators from '../../../shared/custom-validators';
import { TabPanelDisplayMode } from 'app/components/enums/TabPanelDisplayMode';
import { CustomerService } from '../../../services/customer.service';
import { StoreService } from '../../../services/store.service';
import { PartnerCountryService } from '../../../services/partner-country.service';
import { MerchantFieldsService } from '../../../services/merchant-fields.service';
import { LeadSourceService } from '../../../services/lead-source.service';
import { SessionStorageService } from '../../../services/session-storage.service';
import { SupportStatuses } from 'app/models/enums/SupportStatuses';
import { CustomerUserService } from '../../../services/customer-user.service';
import { UserService } from '../../../services/user.service';
import MerchantFieldLabelMap from 'app/models/typescript/MerchantFieldLabelMap';
import { LocalProfileService } from '../../../services/local-profile.service';

@Component({
  selector: 'app-components-sales-addcustomer',
  templateUrl: './add-customer.component.html'
})
export class AddCustomerComponent implements OnInit, IActivatable {
  TabPanelDisplayMode = TabPanelDisplayMode;
  isLoading = false;
  countries: Boo.Objects.Country[] = [];
  languages: Boo.Objects.Language[] = [];
  countryLanguages: Boo.Objects.CountryLanguage[] = [];
  leadSources: Boo.Objects.LeadSource[] = [];
  partner: Boo.Objects.Partner;
  user: Boo.Objects.User;
  merchantFieldLabelMap: MerchantFieldLabelMap = null;
  showMerchantFields = false;
  isCampaignNational: boolean | null = null;
  serviceAreaInput$ = new Subject<string>();
  serviceAreaLoading = false;
  serviceAreas$: Observable<Boo.Objects.LocalProfiles.LocalProfileServiceArea[]> = of([]);

  compareAreas = (a?: Boo.Objects.LocalProfiles.LocalProfileServiceArea, b?: Boo.Objects.LocalProfiles.LocalProfileServiceArea) =>
    !!a && !!b && a.PlaceId === b.PlaceId;

  customerForm = this.fb.group({
    companyName: ['', Validators.required],
    countryId: this.fb.control<number>(null, Validators.required),
    languageId: this.fb.control<number>(null, Validators.required),
    website: ['', [Validators.required, CustomValidators.validUrl()]],
    leadSourceId: this.fb.control<number>(null)
  });

  campaignDetailsForm = this.fb.group({
    isCampaignNational: [null, Validators.required],
    productsOrServices: this.fb.control<string[]>([], Validators.required),
    serviceAreas: this.fb.control<Boo.Objects.LocalProfiles.LocalProfileServiceArea[]>([], [
      Validators.required,
      CustomValidators.maxLengthArrayValidator(20)
    ]),
    avoidFocusingOn: ['']
  });

  primaryContactForm = this.fb.group({
    isPrimary: [true, Validators.required],
    firstName: ['', Validators.required],
    lastName: ['', Validators.required],
    email: ['', [Validators.required, Validators.email]],
    phone: ['', [Validators.required, CustomValidators.phoneNumberValidator(this.countryAbbreviation.bind(this))]],
    canContact: [true, Validators.required],
    canReceiveTextMessages: [false, Validators.required],
    emailRecipientType: [Boo.Objects.Enums.EmailRecipientTypes.To, Validators.required]
  });

  secondaryContactForms = this.fb.array([], CustomValidators.distinctBy(
    control => control.get('email').value, 'Contacts must have different email addresses.'));

  merchantFieldsForm = this.fb.group({
    merchantId: '',
    referredBy: '',
    leadSourceId: null,
    merchantSource: '',
    merchantProposalId: '',
    merchantCategory: '',
    merchantCategoryId: '',
    merchantSubCategory: '',
    merchantSubCategoryId: '',
    merchantConsultant: '',
    merchantConsultantContact: '',
    merchantUdac: '',
    merchantProductCode: '',
    merchantItemId: '',
    merchantMiscId: '',
    merchantRegion: '',
    merchantCustomerValue: '',
    merchantSitePublishDate: this.fb.control<Date>(null),
    merchantContractEndDate: '',
    merchantServiceLevel: '',
    merchantCampaignManager: '',
    merchantCampaignManagerContact: ''
  });

  constructor(
    private fb: FormBuilder,
    private customerService: CustomerService,
    private customerUserService: CustomerUserService,
    private userService: UserService,
    private storeService: StoreService,
    private partnerCountryService: PartnerCountryService,
    private merchantFieldsService: MerchantFieldsService,
    private leadSourceService: LeadSourceService,
    private sessionStorageService: SessionStorageService,
    private localProfileService: LocalProfileService) { }

  ngOnInit(): void {
    this.customerForm.get('countryId').valueChanges
      .subscribe(countryId => this.countryChanged(countryId));

    this.serviceAreas$ = this.serviceAreaInput$.pipe(
      filter(term => !!term),
      distinctUntilChanged(),
      debounceTime(800),
      tap(() => this.serviceAreaLoading = true),
      switchMap(term =>
        this.localProfileService.findServiceAreas(term).pipe(
          catchError(() => of([])),
          finalize(() => this.serviceAreaLoading = false)
        )
      )
    );

    const isNationalCtrl = this.campaignDetailsForm.get('isCampaignNational');
    const serviceAreasCtrl = this.campaignDetailsForm.get('serviceAreas');

    const syncServiceAreaValidators = (isNational: boolean | null) => {
      if (isNational === false) {
        serviceAreasCtrl.setValidators([
          Validators.required,
          CustomValidators.maxLengthArrayValidator(20)
        ]);
      } else {
        serviceAreasCtrl.clearValidators();
        if (serviceAreasCtrl.value?.length) {
          serviceAreasCtrl.setValue([]);
        }
      }
      serviceAreasCtrl.updateValueAndValidity({ emitEvent: false });
    };

    isNationalCtrl.valueChanges
      .pipe(startWith(isNationalCtrl.value))
      .subscribe(syncServiceAreaValidators);

    this.isLoading = true;
    forkJoin([
      this.leadSourceService.get(),
      this.partnerCountryService.get(this.partner.PartnerId),
      this.sessionStorageService.getStaticData(),
      this.merchantFieldsService.generateLabelMapping(this.partner.PartnerId)])
      .pipe(finalize(() => this.isLoading = false))
      .subscribe({
        next: ([leadSources, partnerCountries, staticData, merchantFieldMap]) => {
          this.leadSources = leadSources;
          this.languages = staticData.Languages;
          this.countryLanguages = staticData.CountryLanguages;
          this.countries = staticData.Countries.filter(x => partnerCountries.some(pc => pc.CountryId === x.CountryId)
            && x.SupportStatusId === SupportStatuses.supported);
          this.merchantFieldLabelMap = merchantFieldMap;

          this.customerForm.get('countryId').setValue(this.partner.CountryId);
          this.customerForm.get('languageId').setValue(this.partner.DefaultLanguageId);
        },
        error: (err) => toastr.error(err)
      });
  }

  canActivate(user: Boo.Objects.User, partner: Boo.Objects.Partner): boolean {
    this.partner = partner;
    this.user = user;

    return user.PartnerUsers.some((item) => {
      return item.UserLevelId === Boo.Objects.Enums.UserLevelEnum.Administrator || // Admin
        item.UserLevelId === Boo.Objects.Enums.UserLevelEnum.Manager || // Manager
        item.UserLevelId === Boo.Objects.Enums.UserLevelEnum.Sales; // Sales
    });
  }

  save() {
    this.isLoading = true;

    this.createCustomer().pipe(
      switchMap((customer) => forkJoin(of(customer), this.customerUserService.getForCustomer(customer.CustomerId))),
      switchMap(([customer, customerUsers]) => {
        const promises = [this.primaryContactForm, ...this.secondaryContactForms.controls]
          .map((contact: FormGroup) => {
            const existingUser = customerUsers.find(u => u.Username.toLowerCase() === contact.get('email').value.toLowerCase());

            if (existingUser) {
              return this.userService.save(
                existingUser,
                customer.CustomerId,
                true,
                contact.get('isPrimary').value,
                contact.get('canContact').value,
                contact.get('canReceiveTextMessages').value,
                contact.get('phone').value ? [{
                  PhoneNumber: {
                    Number: contact.get('phone').value,
                    Primary: true
                  }  as Boo.Objects.PhoneNumber
                } as Boo.Objects.CustomerUserPhoneNumber] : [],
                contact.get('emailRecipientType').value);
            }
            else {
              return this.customerUserService.create(
                customer,
                contact.get('email').value,
                contact.get('firstName').value,
                contact.get('lastName').value,
                true,
                contact.get('canContact').value,
                contact.get('canReceiveTextMessages').value,
                contact.get('phone').value ? [{
                    PhoneNumber: {
                      Number: contact.get('phone').value,
                      Primary: true
                    }  as Boo.Objects.PhoneNumber
                  } as Boo.Objects.CustomerUserPhoneNumber] : [],
                contact.get('emailRecipientType').value
              )
            }
          });

        return forkJoin([of(customer), ...promises]);
      }),
      switchMap(([customer, ..._]) => this.storeService.sso(customer.CustomerId, `/place-order`, `#/sales/addcustomer`)),
      finalize(() => this.isLoading = false)
    ).subscribe({
      next: url => window.location.href = url,
      error: err => toastr.error(err)
    });
  }

  addLeadSource(name: string) {
    this.isLoading = true;
    this.leadSourceService.save({
      Name: name,
      LeadSourceId: 0,
      CategoryId: null,
      CategoryName: null,
      CostPerSeat: null,
      RetailPerHour: null,
      RevsharePercent: null,
      RevsharePercentSetup: null,
      WholesalePerHour: null,
    }).pipe(
      switchMap(() => this.leadSourceService.get()),
      finalize(() => this.isLoading = false)
    ).subscribe({
      next:(leadSources) => {
        this.leadSources = leadSources;
        this.customerForm.get('leadSourceId').setValue(leadSources.find(x => x.Name === name).LeadSourceId);
        toastr.success(`Lead Source "${name}" added`);
      },
      error: err => toastr.error(err)
    });
  }

  updateEmailValidity() {
    this.secondaryContactForms.controls.forEach(c => {
      c.get('email').markAsTouched();
      c.get('email').updateValueAndValidity();
    });
  }

  countryAbbreviation() {
    const country = this.countries?.find((x) => x.CountryId === this.customerForm.get('countryId').value);
    return country?.Abbreviation ?? 'US';
  }

  private createCustomer(): Observable<Boo.Objects.Customer> {
      const customer = {
          Name: this.customerForm.get('companyName').value,
          PartnerId: this.partner.PartnerId,
          Email: this.primaryContactForm.get('email').value,
          Phone: this.primaryContactForm.get('phone').value,
          LanguageId: this.customerForm.get('languageId').value,
          CountryId: this.customerForm.get('countryId').value,
          Url: this.customerForm.get('website').value,
          LeadSourceId: this.customerForm.get('leadSourceId').value
      } as Boo.Objects.Customer;

      const merchantFields = {
          MerchantId: this.merchantFieldsForm.get('merchantId').value,
          MerchantSource: this.merchantFieldsForm.get('merchantSource').value,
          MerchantProposalId: this.merchantFieldsForm.get('merchantProposalId').value,
          MerchantCategory: this.merchantFieldsForm.get('merchantCategory').value,
          MerchantCategoryId: this.merchantFieldsForm.get('merchantCategoryId').value,
          MerchantSubCategory: this.merchantFieldsForm.get('merchantSubCategory').value,
          MerchantSubCategoryId: this.merchantFieldsForm.get('merchantSubCategoryId').value,
          MerchantConsultant: this.merchantFieldsForm.get('merchantConsultant').value,
          MerchantConsultantContact: this.merchantFieldsForm.get('merchantConsultantContact').value,
          MerchantUdac: this.merchantFieldsForm.get('merchantUdac').value,
          MerchantProductCode: this.merchantFieldsForm.get('merchantProductCode').value,
          MerchantItemId: this.merchantFieldsForm.get('merchantItemId').value,
          MerchantMiscId: this.merchantFieldsForm.get('merchantMiscId').value,
          MerchantRegion: this.merchantFieldsForm.get('merchantRegion').value,
          MerchantCustomerValue: this.merchantFieldsForm.get('merchantCustomerValue').value,
          MerchantSitePublishDate: this.merchantFieldsForm.get('merchantSitePublishDate').value,
          MerchantContractEndDate: this.merchantFieldsForm.get('merchantContractEndDate').value,
          MerchantServiceLevel: this.merchantFieldsForm.get('merchantServiceLevel').value,
          MerchantCampaignManager: this.merchantFieldsForm.get('merchantCampaignManager').value,
          MerchantCampaignManagerContact: this.merchantFieldsForm.get('merchantCampaignManagerContact').value
      } as Boo.Objects.CustomerMerchantFields;

      const user = {
          FirstName: this.primaryContactForm.get('firstName').value,
          LastName: this.primaryContactForm.get('lastName').value,
          Username: this.primaryContactForm.get('email').value
      } as Boo.Objects.User;

      return this.customerService.create(user, customer, merchantFields, this.campaignDetailsForm.get('isCampaignNational').value, (this.campaignDetailsForm.get('productsOrServices')?.value || []).map((x: any) => x.label), this.campaignDetailsForm.get('serviceAreas').value, this.campaignDetailsForm.get('avoidFocusingOn').value);
  }

  private countryChanged(countryId: number) {
    this.languages = this.countryLanguages.filter(x => x.CountryId === countryId).map(x => x.Language);
    const languageFormControl = this.customerForm.get('languageId');

    if (!this.languages.some((l) => l.LanguageId === languageFormControl.value)) {
      languageFormControl.reset();
    }

    if (this.primaryContactForm.get('phone').touched) {
      this.primaryContactForm.get('phone').updateValueAndValidity();
    }

    this.secondaryContactForms.controls.forEach(c => c.get('phone').updateValueAndValidity());
  }
}
